import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Loader2, Paperclip, Plus, RefreshCw, ShieldAlert } from 'lucide-react';

import { api, documentsAPI, maintenanceAPI } from '../../lib/api';
import { LEXIQUE } from '../../lib/lexique';
import { DOC_COMPONENT_LABELS, DOC_TYPE_LABELS } from '../../lib/maintenanceLabels';
import { libelle, texteSur } from '../../lib/libelles';
import { formatDateOuTiret } from '../../lib/formats';
import { messageErreur } from '../../lib/erreurs';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { usePagination } from '../../hooks/usePagination';
import { inboundRentalsAPI } from '../../lib/inboundRentalsApi';
import { StatutBadge } from '../ui/StatutBadge';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { EmptyState } from '../ui/empty-state';
import { FieldError } from '../ui/field-error';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { LoadingState } from '../ui/loading-state';
import { BarrePagination } from '../ui/pagination-bar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from '../ui/sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

// Rôles qui écrivent dans la maintenance (`_WRITE_ROLES` côté API).
export const DOCUMENT_WRITER_ROLES = ['SUPER_ADMIN', 'ADMIN_SOCIETE', 'LOGISTIQUE', 'MAINTENANCE'];

// Le Select ne connaît pas de valeur vide: « non précisé » a la sienne.
const NO_COMPONENT = 'none';
const TAILLE_MAX = 10 * 1024 * 1024;

// Type de pièce de l'archive générique pour le scan d'un papier.
const ARCHIVE_DOC_TYPE = {
  registration: 'VEHICLE_REGISTRATION',
  insurance: 'INSURANCE',
  technical_inspection: 'TECHNICAL_INSPECTION',
};

const dateLabel = (value) => formatDateOuTiret(value);

const remainingLabel = (doc) => {
  const days = Number(doc.days_remaining);
  if (!Number.isFinite(days)) return '';
  if (days < 0) return `expiré depuis ${Math.abs(days)} j`;
  if (days === 0) return 'expire aujourd’hui';
  return `dans ${days} j`;
};

const emptyDocForm = () => ({
  doc_type: 'insurance',
  component: NO_COMPONENT,
  reference: '',
  issuer: '',
  issued_date: '',
  expiry_date: '',
  alert_days_before: 30,
  blocks_assignment: false,
  // Vide = société (ou camion sans contrat de location reçue).
  cost_bearer: '',
});

// Les fichiers de l'archive sont protégés: un lien direct partirait sans le
// jeton et ne ramènerait qu'une page 401.
const downloadScan = async (scan) => {
  if (!scan?.url) return;
  try {
    const resp = await api.get(scan.url.replace('/api/v1', ''), { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(resp.data);
    const lien = document.createElement('a');
    lien.href = blobUrl;
    lien.download = scan.file_name || 'scan';
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch {
    toast.error('Impossible de télécharger le scan');
  }
};

/**
 * Papiers du camion: assurance, visite technique, carte grise, patente…
 * chacun avec l'élément concerné (tracteur, citerne, ensemble), son échéance
 * et son scan. Remplace l'ancien onglet « Prochaines échéances » de la fiche
 * maintenance, qui ne montrait que les papiers déjà en alerte.
 *
 * Renouveler, c'est enregistrer le nouveau papier: le serveur archive le
 * précédent de même type et même élément.
 */
export default function TruckDocumentsPanel({ truck }) {
  const { user, hasModule } = useAuth();
  const canEdit = DOCUMENT_WRITER_ROLES.includes(user?.role);
  const canAttachScan = hasModule('documents');
  const truckId = truck?.id;

  const [docs, setDocs] = useState([]);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [renewing, setRenewing] = useState(null);

  const load = useCallback(async () => {
    if (!truckId) return;
    setLoading(true);
    setError(false);
    try {
      const [page, archived] = await Promise.all([
        // Tous les papiers du camion, page après page: aucune coupure à 200.
        fetchAllPages(({ page, page_size: taille }) =>
          maintenanceAPI.getVehicleDocumentsPage({ truck_id: truckId, page, page_size: taille }),
        ),
        canAttachScan
          ? documentsAPI
              .getAll({ entity_type: 'truck', entity_id: truckId })
              .catch(() => [])
          : Promise.resolve([]),
      ]);
      setDocs(page.items || []);
      setScans(Array.isArray(archived) ? archived : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [truckId, canAttachScan]);

  useEffect(() => {
    load();
  }, [load]);
  useRafraichirSur(['documents', 'camions'], load);

  const scansById = useMemo(() => {
    const map = new Map();
    scans.forEach((scan) => scan?.id && map.set(scan.id, scan));
    return map;
  }, [scans]);

  // Les pièces rattachées au camion qu'aucun papier ne référence.
  const looseScans = useMemo(() => {
    const linked = new Set(docs.map((doc) => doc.document_id).filter(Boolean));
    return scans.filter((scan) => !linked.has(scan.id));
  }, [docs, scans]);

  // Les papiers et les autres pièces se lisent chacun par pages.
  const pagePapiers = usePagination(docs, { cle: 'papiers' });
  const pagePieces = usePagination(looseScans, { cle: 'pieces' });

  const openCreate = () => {
    setRenewing(null);
    setFormOpen(true);
  };
  const openRenew = (doc) => {
    setRenewing(doc);
    setFormOpen(true);
  };

  if (loading) return <LoadingState shape="table" />;

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Les papiers n’ont pas pu être chargés"
        description="La liste des papiers de ce camion n’est pas disponible pour le moment. Réessayez avant de conclure qu’un papier manque."
        actionLabel="Réessayer"
        onAction={load}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="truck-documents-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base">{LEXIQUE.papiersCamion}</h2>
        {canEdit && (
          <Button size="sm" onClick={openCreate} data-testid="truck-document-add">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Ajouter un papier
          </Button>
        )}
      </div>

      {docs.length === 0 ? (
        <EmptyState
          variant="empty"
          icon={ShieldAlert}
          title="Aucun papier enregistré"
          description="Assurance, visite technique, carte grise, patente, laissez-passer… Chaque papier enregistré ici avec son échéance déclenche un rappel avant d’expirer, avec la plaque du tracteur ou de la citerne concernée."
          actionLabel={canEdit ? 'Ajouter un papier' : undefined}
          onAction={canEdit ? openCreate : undefined}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <Table stickyFirstColumn>
            <TableHeader>
              <TableRow>
                <TableHead>Papier</TableHead>
                <TableHead>Élément</TableHead>
                <TableHead>Référence</TableHead>
                <TableHead>Échéance</TableHead>
                <TableHead>État</TableHead>
                <TableHead>Scan</TableHead>
                {canEdit && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagePapiers.lignes.map((doc) => {
                const scan = doc.document_id ? scansById.get(doc.document_id) : null;
                return (
                  <TableRow key={doc.id} data-testid={`truck-document-${doc.id}`}>
                    <TableCell className="font-medium">
                      {libelle('docVehicule', doc.doc_type)}
                      {doc.issuer ? (
                        <p className="text-xs text-muted-foreground">{texteSur(doc.issuer, '')}</p>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {doc.component ? (
                        libelle('docComponent', doc.component)
                      ) : (
                        <span className="text-muted-foreground">Non précisé</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{doc.reference || '—'}</TableCell>
                    <TableCell>
                      {dateLabel(doc.expiry_date)}
                      <p className="text-xs text-muted-foreground">{remainingLabel(doc)}</p>
                    </TableCell>
                    <TableCell>
                      <StatutBadge domaine="docExpiry" code={doc.expiry_state || 'unknown'} />
                      {doc.blocks_assignment ? (
                        <p className="mt-1 text-xs text-muted-foreground">Bloque l’affectation</p>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {scan ? (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto p-0"
                          onClick={() => downloadScan(scan)}
                        >
                          <Paperclip className="mr-1 h-3 w-3" aria-hidden="true" />
                          Scan
                        </Button>
                      ) : doc.document_id ? (
                        <span className="text-xs text-muted-foreground">Archivé</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => openRenew(doc)}
                        >
                          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                          Renouveler
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <BarrePagination pagination={pagePapiers} nom="papiers" className="mt-0" />

      {looseScans.length > 0 && (
        <section className="space-y-2 rounded-xl border border-border bg-card p-4">
          <h3 className="text-sm font-semibold">Autres pièces du camion</h3>
          <ul className="space-y-1">
            {pagePieces.lignes.map((scan) => (
              <li key={scan.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="truncate">{scan.file_name || 'Pièce'}</span>
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={() => downloadScan(scan)}
                >
                  Télécharger
                </Button>
              </li>
            ))}
          </ul>
          <BarrePagination pagination={pagePieces} nom="pièces" className="mt-0" />
        </section>
      )}

      {canEdit && (
        <VehicleDocumentFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          truck={truck}
          renewing={renewing}
          canAttachScan={canAttachScan}
          onSaved={load}
        />
      )}
    </div>
  );
}

function VehicleDocumentFormDialog({ open, onOpenChange, truck, renewing, canAttachScan, onSaved }) {
  const { hasModule } = useAuth();
  const [form, setForm] = useState(emptyDocForm);
  const [bearerInfo, setBearerInfo] = useState(null);
  const bearerTouched = useRef(false);
  const [file, setFile] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAttempted(false);
    setFile(null);
    setForm(
      renewing
        ? {
            ...emptyDocForm(),
            doc_type: renewing.doc_type || 'insurance',
            component: renewing.component || NO_COMPONENT,
            issuer: renewing.issuer || '',
            alert_days_before: renewing.alert_days_before ?? 30,
            blocks_assignment: !!renewing.blocks_assignment,
            cost_bearer: renewing.cost_bearer || '',
          }
        : emptyDocForm(),
    );
    bearerTouched.current = false;
  }, [open, renewing]);

  // « Qui paie ? » : proposé seulement si la location de camions est active et
  // que ce camion est sous contrat chez un transporteur; pré-rempli d'après le
  // contrat (assurance ou pièces administratives), puis modifiable.
  const rentalsActive = typeof hasModule === 'function' && hasModule('truck_rentals');
  const bearerCategory = form.doc_type === 'insurance' ? 'ASSURANCE' : 'PIECES_ADMIN';
  const truckId = truck?.id;
  useEffect(() => {
    if (!open || !rentalsActive || !truckId) {
      setBearerInfo(null);
      return undefined;
    }
    let cancelled = false;
    inboundRentalsAPI
      .getCostBearer(truckId, bearerCategory, form.issued_date || undefined)
      .then((found) => {
        if (cancelled) return;
        setBearerInfo(found || null);
        if (found?.contract_id) {
          if (!bearerTouched.current) {
            setForm((prev) => ({ ...prev, cost_bearer: found.cost_bearer || 'SOCIETE' }));
          }
        } else {
          setForm((prev) => (prev.cost_bearer ? { ...prev, cost_bearer: '' } : prev));
        }
      })
      .catch(() => {
        if (!cancelled) setBearerInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, rentalsActive, truckId, bearerCategory, form.issued_date]);
  const bearerVisible = rentalsActive && Boolean(bearerInfo?.contract_id);

  const set = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));
  const datesInverted =
    form.issued_date && form.expiry_date && form.issued_date > form.expiry_date;

  const submit = async () => {
    setAttempted(true);
    if (!form.expiry_date || datesInverted) return;
    setBusy(true);
    try {
      let documentId;
      if (file) {
        if (file.size > TAILLE_MAX) {
          toast.error('Fichier trop lourd — 10 Mo maximum.');
          return;
        }
        // Le scan d'abord: un papier enregistré sans le fichier choisi
        // laisserait croire qu'il est archivé.
        const uploaded = await documentsAPI.upload(
          file,
          ARCHIVE_DOC_TYPE[form.doc_type] || 'OTHER',
          { entity_type: 'truck', entity_id: truck.id, capture_source: 'web_operator' },
        );
        documentId = uploaded?.id;
      }
      const alertDays = Number(form.alert_days_before);
      await maintenanceAPI.createVehicleDocument({
        truck_id: truck.id,
        doc_type: form.doc_type,
        component: form.component === NO_COMPONENT ? null : form.component,
        reference: form.reference.trim() || null,
        issuer: form.issuer.trim() || null,
        issued_date: form.issued_date || null,
        expiry_date: form.expiry_date,
        alert_days_before: Number.isFinite(alertDays) && alertDays >= 0 ? alertDays : 30,
        blocks_assignment: !!form.blocks_assignment,
        cost_bearer: bearerVisible && form.cost_bearer ? form.cost_bearer : null,
        ...(documentId ? { document_id: documentId } : {}),
      });
      toast.success(
        renewing ? 'Papier renouvelé — l’ancien est archivé' : 'Papier enregistré',
      );
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(messageErreur(err, 'Le papier n’a pas pu être enregistré.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            {renewing
              ? `Renouveler : ${libelle('docVehicule', renewing.doc_type)}`
              : 'Ajouter un papier'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="label-base">Papier *</Label>
              <Select
                value={form.doc_type}
                onValueChange={(v) => setForm((prev) => ({ ...prev, doc_type: v }))}
                disabled={!!renewing}
              >
                <SelectTrigger className="input-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DOC_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-base">Élément</Label>
              <Select
                value={form.component}
                onValueChange={(v) => setForm((prev) => ({ ...prev, component: v }))}
                disabled={!!renewing}
              >
                <SelectTrigger className="input-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_COMPONENT}>Non précisé</SelectItem>
                  {Object.entries(DOC_COMPONENT_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="label-base">Référence</Label>
              <Input className="input-base" value={form.reference} onChange={set('reference')} />
            </div>
            <div>
              <Label className="label-base">Émetteur</Label>
              <Input
                className="input-base"
                value={form.issuer}
                onChange={set('issuer')}
                placeholder="Ex: compagnie d’assurance"
              />
            </div>
            <div>
              <Label className="label-base">Date d’émission</Label>
              <Input
                type="date"
                className="input-base"
                value={form.issued_date}
                onChange={set('issued_date')}
              />
            </div>
            <div>
              <Label className="label-base">Échéance *</Label>
              <Input
                type="date"
                className="input-base"
                value={form.expiry_date}
                onChange={set('expiry_date')}
                data-testid="truck-document-expiry"
              />
              <FieldError show={attempted && !form.expiry_date}>Échéance requise</FieldError>
              <FieldError show={!!datesInverted}>
                L’échéance doit suivre la date d’émission
              </FieldError>
            </div>
            <div>
              <Label className="label-base">Prévenir (jours avant)</Label>
              <Input
                type="number"
                min="0"
                className="input-base"
                value={form.alert_days_before}
                onChange={set('alert_days_before')}
              />
            </div>
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.blocks_assignment}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, blocks_assignment: e.target.checked }))
                }
              />
              Bloque l’affectation à expiration
            </label>
            {bearerVisible && (
              <div className="md:col-span-2" data-testid="truck-document-cost-bearer">
                <Label className="label-base">Qui paie ?</Label>
                <Select
                  value={form.cost_bearer || 'SOCIETE'}
                  onValueChange={(v) => {
                    bearerTouched.current = true;
                    setForm((prev) => ({ ...prev, cost_bearer: v }));
                  }}
                >
                  <SelectTrigger className="input-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['SOCIETE', 'PROPRIETAIRE'].map((code) => (
                      <SelectItem key={code} value={code}>
                        {libelle('porteur_frais', code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {bearerInfo?.lessor_name ? (
                  <div className="mt-1">
                    <Aide titre="Qui paie ?" libelle={`Camion loué chez ${bearerInfo.lessor_name}`}>
                      Proposé d’après son contrat, vous pouvez le changer.
                    </Aide>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {canAttachScan && (
            <div>
              <Label className="label-base">Scan du papier</Label>
              <Input
                type="file"
                accept="image/*,application/pdf"
                className="input-base"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="truck-document-file"
              />
              <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                Photo ou PDF, 10 Mo maximum.
                <Aide titre="Scan du papier">Le scan est rattaché au camion.</Aide>
              </p>
            </div>
          )}

          {renewing && (
            <Aide titre="Renouvellement" libelle="Ce que devient le papier actuel">
              Le papier actuel (échéance {dateLabel(renewing.expiry_date)}) sera archivé et ne
              déclenchera plus de rappel.
            </Aide>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Annuler
          </Button>
          <Button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
